"use client";

import { useState, useEffect, useMemo } from "react";

import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { HeroSection } from "@/components/hero-section";
import { useInView } from "@/hooks/useInView";
import { useLanguage } from "@/context/LanguageContext";
import { contentInfoApi, webInfoApi } from "@/lib/api";

import dynamic from "next/dynamic";

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

interface Section {
  number: number;
  title: string;
  content: string[];
  icon: string;
  anchor: string;
}


const ICONS: Record<number, string> = {
  1: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" /></svg>`,
  2: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`,
  3: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>`,
  4: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" /></svg>`,
  5: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>`,
  6: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>`,
  7: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" /></svg>`,
  8: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>`,
  9: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>`,
  10: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>`,
  11: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>`,
};

const slugify = (text: string) => text.toLowerCase().replace(/[^\w]+/g, "-");

function parseTerms(content: string) {
  const lines = (content || "").replace(/\r\n/g, "\n").split("\n");

  let title = "Terms of Service";
  let effectiveDate = "";
  const sections: Section[] = [];

  let current: Section | null = null;

  for (const line of lines) {
    // Page title
    if (line.startsWith("# ")) {
      title = line.replace("# ", "").trim();
      continue;
    }

    // Effective Date
    if (line.includes("Effective Date")) {
      effectiveDate = line.replace(/\*\*/g, "").trim();
      continue;
    }

    // Section header: ## 1. Title
    const sectionMatch = line.match(/^##\s+(\d+)\.\s+(.+)/);
    if (sectionMatch) {
      if (current) sections.push(current);

      const number = Number(sectionMatch[1]);
      const sectionTitle = sectionMatch[2];

      current = {
        number,
        title: sectionTitle,
        content: [],
        icon: ICONS[number] || "📄",
        anchor: `section-${number}-${slugify(sectionTitle)}`,
      };
      continue;
    }

    // Section body
    if (current && line.trim() !== "") {
      current.content.push(line);
    }
  }

  if (current) sections.push(current);

  // fallback
  if (sections.length === 0 && content?.trim()) {
    sections.push({
      number: 1,
      title,
      content: lines.filter((l) => l.trim() !== ""),
      icon: "📄",
      anchor: `section-1-${slugify(title)}`,
    });
  }

  return { title, effectiveDate, sections };
}

export default function TermsOfService() {
  const { language } = useLanguage();
  const [rawContent, setRawContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroContent, setHeroContent] = useState<{
    content_title: string;
    content_description?: string | null;
  } | null>(null);

  // ✅ same as your Privacy Policy page
  const [contentRef, contentInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await webInfoApi.getAll({ lang: language });
        const data = res.data?.data || res.data;
        setRawContent(data?.[0]?.terms_of_service || "");
      } catch {
        setError("Failed to load Terms of Service.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [language]);

  useEffect(() => {
    let isMounted = true;

    const fetchHeroContent = async () => {
      try {
        const response = await contentInfoApi.getAll({ limit: 1, type: "terms-of-service", lang: language });
        const data = (response.data?.data || response.data) as Array<{
          content_title: string;
          content_description?: string | null;
        }>;

        if (!isMounted) return;
        setHeroContent(data?.[0] || null);
      } catch (err) {
        if (isMounted) setHeroContent(null);
        console.log("Error fetching terms hero content:", err);
      }
    };

    fetchHeroContent();
    return () => {
      isMounted = false;
    };
  }, [language]);

  const parsed = useMemo(() => parseTerms(rawContent), [rawContent]);

  const renderContent = (lines: string[]) => {
    const nodes: React.ReactNode[] = [];
    let bulletBuffer: string[] = [];

    const flushBullets = (keyPrefix: string) => {
      if (bulletBuffer.length === 0) return;
      nodes.push(
        <ul key={`${keyPrefix}-ul`} className="list-disc pl-6 mb-4">
          {bulletBuffer.map((b, i) => (
            <li
              key={`${keyPrefix}-li-${i}`}
              className="text-gray-700 mb-2 last:mb-0"
              dangerouslySetInnerHTML={{ __html: b }}
            />
          ))}
        </ul>
      );
      bulletBuffer = [];
    };

    lines.forEach((rawLine, idx) => {
      const line = rawLine.trim();
      if (!line) return;

      const htmlLine = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

      if (line.startsWith("- ")) {
        bulletBuffer.push(htmlLine.replace(/^- /, ""));
        return;
      }

      flushBullets(`b-${idx}`);

      nodes.push(
        <p
          key={`p-${idx}`}
          className="text-gray-700 mb-4 last:mb-0 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: htmlLine }}
        />
      );
    });

    flushBullets("end");
    return <div>{nodes}</div>;
  };

  // ✅ Animated TOC component (same pattern as Privacy Policy)
  const TableOfContents = () => {
    const [tocRef, tocInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

    return (
      <div
        ref={tocRef}
        className={`bg-white rounded-lg border-2 border-[#0C2E6C] shadow-sm p-8 mb-8 -mt-30 reveal ${tocInView ? "is-visible" : ""
          }`}
      >
        <h2 className="text-2xl font-bold text-[#0C2E6C] mb-6">Table of Contents</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {parsed.sections.map((section) => (
            <a
              key={section.anchor}
              href={`#${section.anchor}`}
              className="flex items-center gap-3 text-gray-700 hover:text-[#0C2E6C] transition-colors p-2 rounded hover:bg-[#E6F0FF]"
            >
              <span className="w-6 h-6 shrink-0" dangerouslySetInnerHTML={{ __html: section.icon }} />
              <span className="text-sm">
                {section.number}. {section.title}
              </span>
            </a>
          ))}
        </div>
      </div>
    );
  };

  // ✅ Animated section card
  const SectionCard = ({ section }: { section: Section }) => {
    const [sectionRef, sectionInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

    return (
      <div
        id={section.anchor}
        ref={sectionRef}
        className={`bg-white border rounded-lg p-8 mb-6 shadow-sm reveal scroll-mt-20 ${sectionInView ? "is-visible" : ""
          }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-8 shrink-0" dangerouslySetInnerHTML={{ __html: section.icon }} />
          <h2 className="text-2xl font-bold">
            {section.number}. {section.title}
          </h2>
        </div>

        {renderContent(section.content)}
      </div>
    );
  };

  // ✅ Animated contact card
  const ContactCard = () => {
    const [contactRef, contactInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

    return (
      <div
        ref={contactRef}
        className={`bg-white border rounded-lg p-8 mt-10 shadow-sm reveal ${contactInView ? "is-visible" : "is-visible"
          }`}
      >
        <p className="text-sm text-gray-600">
          {language === "id"
            ? "Jika Anda memiliki pertanyaan tentang Ketentuan Layanan ini, silakan hubungi kami di "
            : "If you have any questions about these Terms of Service, please contact us at "}
          <a href="mailto:info@ganeshait.com" className="text-blue-600 underline">
            info@ganeshait.com
          </a>
        </p>
      </div>
    );
  };

  return (
    <div className="page-layout bg-white">
      <div className="page-content">
        <Header />

        <HeroSection
          title={heroContent?.content_title || parsed.title}
          subtitle={heroContent?.content_description || "Please read these terms carefully before using our services."}
        />

        <section className="py-12 px-8 lg:px-16 bg-white">
          <div className="max-w-6xl mx-auto">
            {loading && (
              <div className="flex justify-center py-12">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-gray-800" />
              </div>
            )}

            {error && <p className="text-center text-red-600">{error}</p>}

            {!loading && !error && (
              <>
                {/* ✅ Effective Date (animated like Privacy Policy) */}
                <div
                  ref={contentRef}
                  className={`bg-white border rounded-lg p-6 mb-8 shadow-sm reveal ${contentInView ? "is-visible" : ""
                    }`}
                >
                  <p className="text-sm text-gray-500">
                    {parsed.effectiveDate ||
                      `Last Updated: ${new Date().toLocaleDateString(language === "id" ? "id-ID" : "en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}`}
                  </p>
                </div>

                {/* ✅ TOC (animated) */}
                <TableOfContents />

                {/* ✅ Sections (animated) */}
                {parsed.sections.map((section) => (
                  <SectionCard key={section.anchor} section={section} />
                ))}

                {/* ✅ Contact (animated) */}
                <ContactCard />
              </>
            )}
          </div>
        </section>
      </div>

      <Footer />
      <ChatWidget />
    </div>
  );
}
